import React, { useState, useEffect } from "react";
import { getCommandCentre, approveIntelAction, dismissIntelAction } from "../../../api";
import { ScoreBadge, PriorityTag, money } from "./bits";
import OwnerIntelReport from "./OwnerIntelReport";
import { Sparkles, Building2, TrendingUp, Users, AlertTriangle, Wrench, Check, X, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";

const PriorityCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-[#f0ece5] rounded-xl p-4 flex items-center gap-3" data-testid={`priority-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className={`w-11 h-11 rounded-xl grid place-items-center ${accent}`}><Icon size={20} /></div>
    <div>
      <div className="text-[24px] font-bold text-[#2b2b2b] leading-none">{value}</div>
      <div className="text-[12px] text-[#8a8a8a] mt-1">{label}</div>
    </div>
  </div>
);

const AdminCommandCentre = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setData(await getCommandCentre()); }
    catch { toast({ title: "Couldn't load the command centre", variant: "destructive" }); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const approve = async (a) => {
    setBusy(a.id);
    try {
      await approveIntelAction(a.id);
      toast({ title: "Approved & executed", description: a.action });
      setData((d) => ({ ...d, suggestions: d.suggestions.filter((s) => s.id !== a.id) }));
    } catch { toast({ title: "Couldn't execute action", variant: "destructive" }); }
    setBusy(null);
  };
  const dismiss = async (a) => {
    setBusy(a.id);
    try {
      await dismissIntelAction(a.id);
      setData((d) => ({ ...d, suggestions: d.suggestions.filter((s) => s.id !== a.id) }));
    } catch { toast({ title: "Couldn't dismiss", variant: "destructive" }); }
    setBusy(null);
  };

  if (loading || !data) return <div className="py-20 grid place-items-center text-[#8a8a8a]" data-testid="command-centre-loading"><Loader2 className="animate-spin" /></div>;

  const P = data.priorities;
  return (
    <div className="space-y-6" data-testid="admin-command-centre">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#b8935f] font-medium flex items-center gap-1.5"><Sparkles size={13} /> Anadear Intelligence</div>
        <h2 className="text-[26px] font-semibold text-[#2b2b2b] mt-1">{data.greeting}</h2>
        <p className="text-[13px] text-[#8a8a8a]">Here's where your attention is worth the most today.</p>
      </div>

      <div>
        <div className="text-[12px] font-medium text-[#6a6a6a] mb-2">Highest priority</div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <PriorityCard icon={Building2} label="Landlord opportunities" value={P.landlordOpportunities} accent="bg-[#faf3e9] text-[#b8935f]" />
          <PriorityCard icon={TrendingUp} label="Seller opportunities" value={P.sellerOpportunities} accent="bg-blue-50 text-blue-600" />
          <PriorityCard icon={Users} label="Buyers to follow up" value={P.buyersToFollowUp} accent="bg-purple-50 text-purple-600" />
          <PriorityCard icon={AlertTriangle} label="Transactions at risk" value={P.transactionsAtRisk} accent="bg-red-50 text-red-600" />
          <PriorityCard icon={Wrench} label="Maintenance issues" value={P.maintenanceIssues} accent="bg-amber-50 text-amber-600" />
        </div>
      </div>

      {/* AI suggestions — human approval */}
      <div className="bg-[#2b2b2b] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1"><Sparkles size={16} className="text-[#b8935f]" /><h3 className="text-[16px] font-semibold text-white">AI suggestions</h3></div>
        <p className="text-[12px] text-[#a8a8a8] flex items-center gap-1.5 mb-4"><ShieldCheck size={13} /> AI recommends — you approve — the system executes.</p>
        {data.suggestions.length === 0 ? (
          <div className="text-[13px] text-[#a8a8a8] py-4">All caught up — no pending suggestions.</div>
        ) : (
          <div className="space-y-2">
            {data.suggestions.map((a) => (
              <div key={a.id} data-testid={`suggestion-${a.id}`} className="bg-[#353535] rounded-xl p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] text-white font-medium">{a.action}</span>
                    <PriorityTag priority={a.priority} />
                  </div>
                  <div className="text-[12px] text-[#b8935f] mt-0.5">{a.entityName}</div>
                  <div className="text-[12px] text-[#a8a8a8] mt-0.5">{a.reason}</div>
                </div>
                <button onClick={() => approve(a)} disabled={busy === a.id} data-testid={`approve-${a.id}`} className="flex items-center gap-1.5 text-[13px] bg-[#b8935f] hover:bg-[#a37f4e] text-white px-3 py-2 rounded-lg disabled:opacity-60">
                  {busy === a.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Approve
                </button>
                <button onClick={() => dismiss(a)} disabled={busy === a.id} data-testid={`dismiss-${a.id}`} className="text-[#a8a8a8] hover:text-white p-2 rounded-lg hover:bg-[#454545]"><X size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top opportunities lists */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#f0ece5] rounded-xl p-4">
          <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><Building2 size={16} className="text-[#b8935f]" /> Top landlord opportunities</h3>
          <div className="space-y-2">
            {data.topLandlords.map((o) => (
              <button key={o.id} onClick={() => setOpenId(o.id)} data-testid={`cc-landlord-${o.id}`} className="w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-[#faf7f2]">
                <ScoreBadge score={o.score} />
                <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-[#2b2b2b] truncate">{o.owner}</div><div className="text-[12px] text-[#8a8a8a] truncate">{Math.round(o.pLandlord * 100)}% landlord · {money(o.estAnnualMgmtRevenue)}/yr</div></div>
                <ArrowRight size={15} className="text-[#b8935f]" />
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#f0ece5] rounded-xl p-4">
          <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-[#b8935f]" /> Likely sellers</h3>
          <div className="space-y-2">
            {data.topSellers.map((o) => (
              <button key={o.id} onClick={() => setOpenId(o.id)} data-testid={`cc-seller-${o.id}`} className="w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-[#faf7f2]">
                <ScoreBadge score={o.score} />
                <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-[#2b2b2b] truncate">{o.owner}</div><div className="text-[12px] text-[#8a8a8a] truncate">{Math.round(o.p12m * 100)}% list ≤12m · equity {money(o.estEquity)}</div></div>
                <ArrowRight size={15} className="text-[#b8935f]" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-[#a8a8a8] italic">Opportunity scores and financial figures are model estimates from available signals — not guaranteed valuations or advice.</p>
      <OwnerIntelReport ownerId={openId} open={!!openId} onOpenChange={(v) => !v && setOpenId(null)} onActioned={load} />
    </div>
  );
};

export default AdminCommandCentre;
